import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { shallow } from "zustand/shallow";

import { getAverageScore } from "utils";
import useStore from "components/PerformanceReview/store";

/**
 * Question pills for the feedback view — mobile port of desktop `TopNavigation`.
 *
 * Pills scroll horizontally and colour by outcome: red when the question errored
 * or scored low (<50 overall), green otherwise; the active pill is filled.
 * Active pill auto-scrolls into view.
 *
 * The next-question chevron only appears when the pills actually overflow the
 * strip — measured from the live geometry (scrollWidth > clientWidth), not a
 * fixed pill count or width breakpoint, so it adapts to any screen size and to
 * rotation/resize via a ResizeObserver.
 */
export default function QuestionNav() {
  const questions = useStore((s) => s.questions, shallow);
  const currentQuestion = useStore((s) => s.currentQuestion);
  const setCurrentQuestion = useStore((s) => s.setCurrentQuestion);

  const scrollerRef = React.useRef(null);
  const activeRef = React.useRef(null);
  const [overflowing, setOverflowing] = React.useState(false);

  // Re-measure overflow on mount, whenever the pill count changes, and whenever
  // the strip resizes (viewport width change / rotation / font scaling).
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const measure = () =>
      setOverflowing(scroller.scrollWidth > scroller.clientWidth + 1);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [questions.length]);

  // Centre the active pill by scrolling the strip horizontally only — never
  // `scrollIntoView`, which also scrolls the page vertically and would jump the
  // whole feedback page down to this section on load.
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;

    const target =
      active.offsetLeft - scroller.clientWidth / 2 + active.offsetWidth / 2;
    scroller.scrollTo({ left: Math.max(target, 0), behavior: "smooth" });
  }, [currentQuestion]);

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        ref={scrollerRef}
        sx={{
          position: "relative",
          display: "flex",
          gap: 2,
          overflowX: "auto",
          flex: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {questions.map((q, index) => {
          const isActive = index === currentQuestion;
          const overall = getAverageScore(
            q?.communication?.ratings?.OVERALL || 0,
            q?.content?.ratings?.OVERALL || 0
          );
          const isLow = q?.isError || overall < 50;

          const palette = isLow
            ? { fg: "#C51407", bg: "#FFE6E3", activeBg: "#C51407" }
            : { fg: "#00664A", bg: "#DCFCE7", activeBg: "#009970" };

          return (
            <Box
              key={index}
              ref={isActive ? activeRef : null}
              role="button"
              onClick={() => setCurrentQuestion(index)}
              sx={{
                flex: "0 0 auto",
                minWidth: 48,
                py: 1.5,
                px: 2,
                borderRadius: 2,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: isActive ? palette.activeBg : palette.bg,
                color: isActive ? "#FFFFFF" : palette.fg,
              }}
            >
              <Typography variant="body01-semiBold" color="inherit">
                Q{index + 1}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Only shown when the pills actually overflow the strip. */}
      {overflowing && (
        <IconButton
          onClick={handleNext}
          disabled={currentQuestion >= questions.length - 1}
          size="small"
          aria-label="Next question"
          sx={{ color: "neutral.clr-700", flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
}
